import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { authActions } from "@_actions/auth.actions";
import { useTranslation } from "react-i18next";
import styles from "./style.module.css";
import { TextField } from "@mui/material";
import Progress from "@_components/Progress/Progress";
import Background from "@_assets/images/default_login_page.jpg";
import Error from "@_pages/Error/Error";

function LoginPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.authReducer);
  const customerDetails = useSelector((state) => state.customerReducer);

  const [inputs, setInputs] = useState({
    caseNumber: "",

    pin: "",
  });

  const [clientValidationError, setClientValidationError] = useState(false);

  const handleSubmit = useCallback(
    function handleSubmit(e) {
      if (e) e.preventDefault();
      const caseTrimmed = String(inputs.caseNumber ?? "").replace(/\s+/g, "");
      const pinTrimmed = String(inputs.pin ?? "").replace(/\s+/g, "");
      if (!caseTrimmed || !pinTrimmed) {
        setClientValidationError(true);
        return;
      }
      setClientValidationError(false);
      // save config cache
      let cache = sessionStorage.getItem("configCache");
      const searchParams = new URLSearchParams(window.location.search);
      if (cache !== null) sessionStorage.setItem("configCache", cache);
      dispatch(
        authActions.login(
          caseTrimmed,
          pinTrimmed,
          window.location.hostname,
          searchParams.get("user_id") || "",
        ),
      );
    },
    [dispatch, inputs],
  );

  const inputErrorState = Boolean(auth.message) || clientValidationError;

  // Adding a keydown handler event to trigger submit when pressing enter
  useEffect(() => {
    const keyDownHandler = (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        handleSubmit(event);
      }
    };

    document.addEventListener("keydown", keyDownHandler);

    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [handleSubmit]);

  useEffect(() => {
    if (!sessionStorage.getItem("user")) {
      dispatch(authActions.logout());
    }
  }, [dispatch]);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const caseId = searchParams.get("case_id");
    if (
      (searchParams.has("user_id") ||
        (searchParams.has("auto_login") && searchParams.get("auto_login"))) &&
      searchParams.has("case_pin")
    ) {
      setInputs({
        caseNumber: caseId ?? "",
        pin: searchParams.get("case_pin") ?? "",
      });
    } else if (caseId) {
      setInputs((prev) => ({ ...prev, caseNumber: caseId }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.has("user_id") || searchParams.has("auto_login")) {
      handleSubmit();
    }
    // eslint-disable-next-line
  }, [inputs]);

  // Check if we have real backend data (more than just the default keys)
  // We check for specific fields that only come from a successful backend response
  const hasRealCustomerData =
    customerDetails.customer &&
    (customerDetails.customer.name ||
      customerDetails.customer.logo_url ||
      Object.keys(customerDetails.customer).length > 2);

  if (auth.loading) {
    return <Progress />;
  }

  // Show loading/blank page while customer details are being fetched
  if (customerDetails.loading) {
    return <Progress />;
  }

  // Show error page if customer details fetch failed
  const hasError = (() => {
    const { message } = customerDetails;

    if (!message || message === "") return false;

    // If it's an object, check for error properties
    if (typeof message === "object") {
      return (
        message.error_code !== undefined ||
        message.error_message ||
        message.message
      );
    }

    // String, number, or other truthy value indicates an error
    return true;
  })();

  if (hasError) {
    return <Error />;
  }

  // Only show login form if we have real customer data loaded
  if (!hasRealCustomerData) {
    return <Progress />;
  }

  return (
    <div className={styles.login_page}>
      <div className={styles.background_container}>
        <img
          src={
            customerDetails?.customer?.login_page_image_url ||
            (!customerDetails.loading && Background)
          }
          alt=""
        />
      </div>
      <div className={styles.login_page_content}>
        <div className={styles.login_form_column}>
          <div className={styles.login_text_container}>
            <h2 className={styles.login_header_text}>
              {t("_you_received_an_email_from_us")}
            </h2>
            <p className={styles.login_text}>{t("_login_text")}</p>
          </div>
          <div className={styles.login_input_container}>
            <TextField
              value={inputs.caseNumber ?? ""}
              onChange={(e) => {
                setClientValidationError(false);
                setInputs({ ...inputs, caseNumber: e.target.value });
              }}
              variant="outlined"
              size="small"
              error={inputErrorState}
              label={t("_case_number")}
              type="text"
              placeholder={t("_case_number")}
              classes={{ root: styles.login_input }}
            />
          </div>
          <div className={styles.login_input_container}>
            <TextField
              value={inputs.pin ?? ""}
              onChange={(e) => {
                setClientValidationError(false);
                setInputs({ ...inputs, pin: e.target.value });
              }}
              variant="outlined"
              size="small"
              error={inputErrorState}
              label={t("_pin")}
              type="password"
              placeholder={t("_pin")}
              classes={{
                root: styles.login_input,
              }}
            />
          </div>
          <div>
            {(clientValidationError || auth.message) && (
              <div className={styles.login_error}>
                {clientValidationError ? (
                  <>{t("_case_number_or_password_is_incorrect")}</>
                ) : auth.message ===
                  "Access forbidden: case is being revised." ? (
                  <>{t("_case_reviewed_text")}</>
                ) : (
                  <>{t("_case_number_or_password_is_incorrect")}</>
                )}
              </div>
            )}
          </div>
          <div className={styles.login_btn_container}>
            <button
              className={styles.login_btn}
              onClick={handleSubmit}
              disabled={auth.loading}
              type="submit"
            >
              {t("_show_case_details")}
            </button>
          </div>
          <div className={styles.login_info_text}>
            {customerDetails?.customer?.login_text && (
              <p>{customerDetails.customer.login_text}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
